import { byRank, isScene, narrativeOrder, outlineChildren, rankBetween, type StoryCtx, type StoryNode } from '@dispositio/core';

/**
 * Neue Stelle im Manuskript: `within` ist bei einer Szene ihr Kapitel (null = nicht eingeordnet), bei einem Kapitel das Manuskript oder
 * ein Abschnitt (`section`), bei einem Abschnitt das Manuskript; `rank` der Sortierschlüssel darin (ohne Kapitel: null).
 */
export interface Placement {
  within: string | null;
  rank: string | null;
  /** `within` ist ein Abschnitt */
  section?: boolean;
}

/** Was in `within` steht: im Manuskript die Abschnitte und Kapitel, im Abschnitt seine Kapitel, im Kapitel seine Szenen (über ihr `chapterId`). */
function membersOf(ctx: StoryCtx, within: string): StoryNode[] {
  const kind = ctx.nodes.get(within)?.kind;
  if (kind === 'manuscript' || kind === 'section') return outlineChildren(ctx, within);
  return ctx.story.nodes.filter((n) => isScene(n) && n.chapterId === within);
}

/** Stelle `index` in `within` (ohne `movingId` gezählt): Sortierschlüssel zwischen den Nachbarn. */
export function placeAt(ctx: StoryCtx, within: string, index: number, movingId?: string): Placement {
  const siblings = byRank(membersOf(ctx, within).filter((n) => n.id !== movingId && n.rank));
  const i = Math.max(0, Math.min(index, siblings.length));
  return { within, rank: rankBetween(siblings[i - 1]?.rank ?? null, siblings[i]?.rank ?? null), ...(ctx.nodes.get(within)?.kind === 'section' ? { section: true } : {}) };
}

/** Szene eine Stelle nach oben (-1) oder unten (1); am Rand eines Kapitels ans Ende des vorigen bzw. an den Anfang des nächsten. */
export function stepScene(ctx: StoryCtx, sceneId: string, dir: -1 | 1): Placement | null {
  const chapters = narrativeOrder(ctx).chapters;
  const c = chapters.findIndex((ch) => ch.scenes.some((s) => s.scene.id === sceneId));
  if (c < 0) return null;
  const scenes = chapters[c]!.scenes;
  const i = scenes.findIndex((s) => s.scene.id === sceneId);
  const j = i + dir;
  if (j >= 0 && j < scenes.length) return placeAt(ctx, chapters[c]!.chapter.id, j, sceneId);
  const neighbour = chapters[c + dir];
  if (!neighbour) return null;
  return placeAt(ctx, neighbour.chapter.id, dir < 0 ? neighbour.scenes.length : 0, sceneId);
}

/**
 * Kapitel eine Stelle nach oben (-1) oder unten (1). Steht daneben ein Abschnitt, rückt es hinein (an sein Ende bzw. seinen Anfang); am
 * Rand seines Abschnitts rückt es heraus, vor bzw. hinter den Abschnitt.
 */
export function stepChapter(ctx: StoryCtx, chapterId: string, dir: -1 | 1): Placement | null {
  const node = ctx.nodes.get(chapterId);
  if (!node?.parentId) return null;
  const siblings = byRank(membersOf(ctx, node.parentId).filter((n) => n.rank));
  const i = siblings.findIndex((s) => s.id === chapterId);
  if (i < 0) return null;
  const neighbour = siblings[i + dir];
  if (neighbour?.kind === 'section') return placeAt(ctx, neighbour.id, dir < 0 ? membersOf(ctx, neighbour.id).length : 0, chapterId);
  if (neighbour) return placeAt(ctx, node.parentId, i + dir, chapterId);
  const parent = ctx.nodes.get(node.parentId);
  if (parent?.kind !== 'section' || !parent.parentId) return null;
  const outer = byRank(membersOf(ctx, parent.parentId).filter((n) => n.rank));
  const k = outer.findIndex((s) => s.id === parent.id);
  return placeAt(ctx, parent.parentId, dir < 0 ? k : k + 1, chapterId);
}

/** Abschnitt eine Stelle nach oben (-1) oder unten (1) – zwischen den Abschnitten und Kapiteln im Manuskript. */
export function stepSection(ctx: StoryCtx, sectionId: string, dir: -1 | 1): Placement | null {
  const node = ctx.nodes.get(sectionId);
  if (!node?.parentId) return null;
  const siblings = byRank(membersOf(ctx, node.parentId).filter((n) => n.rank));
  const i = siblings.findIndex((s) => s.id === sectionId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= siblings.length) return null;
  return placeAt(ctx, node.parentId, j, sectionId);
}

/** Stelle vor `beforeId` (null: ans Ende) in `within`, ohne den verschobenen Node gezählt; `within` null: nicht eingeordnet. */
export function placeBefore(ctx: StoryCtx, within: string | null, beforeId: string | null, movingId: string): Placement {
  if (within === null) return { within: null, rank: null };
  const siblings = byRank(membersOf(ctx, within).filter((n) => n.id !== movingId && n.rank));
  const index = beforeId === null ? siblings.length : siblings.findIndex((s) => s.id === beforeId);
  return placeAt(ctx, within, index < 0 ? siblings.length : index, movingId);
}

/**
 * Änderung für den Server: Sortierschlüssel; bei einer Szene in einem anderen Kapitel auch das Kapitel, bei einem Kapitel in einem anderen
 * Abschnitt (bzw. heraus ins Manuskript) auch der Abschnitt. Null, wenn sich nichts ändert.
 */
export function placementPatch(node: StoryNode, p: Placement | null): { rank?: string; chapterId?: string | null; sectionId?: string | null } | null {
  if (!p) return null;
  if (node.kind === 'section') return p.rank && p.rank !== node.rank ? { rank: p.rank } : null;
  if (node.kind === 'chapter') {
    if (p.within !== null && p.within !== node.parentId) return { sectionId: p.section ? p.within : null, rank: p.rank! };
    return p.rank && p.rank !== node.rank ? { rank: p.rank } : null;
  }
  if (p.within !== (node.chapterId ?? null)) return p.within === null ? { chapterId: null } : { chapterId: p.within, rank: p.rank! };
  return p.rank && p.rank !== node.rank ? { rank: p.rank } : null;
}
